/**
 * g.action - THE one way to start async work from a control.
 *
 *   btn.addEventListener('click', () => g.action(btn, async () => {
 *     const ok = await g.confirm('Delete it?', { danger: true });
 *     if (!ok) return;
 *     const res = await g.fetch('/things/delete', { id });
 *     if (!g.outcome(res)) return;
 *     await list.reload();
 *   }));
 *
 * What it does:
 *   - RE-ENTRY IS A NO-OP: while fn's promise is pending, calling g.action
 *     again with the same lock key returns the RUNNING promise and starts
 *     nothing (a double/triple click = one request);
 *   - BUSY IN THE CONTROL for the whole flight (g.busy: the spinner replaces
 *     the button's icon, width frozen, aria-busy, disabled) - a g.confirm
 *     inside fn and an awaited reload included; released when fn settles;
 *   - KEYED LOCKS: the lock key defaults to the target element; opts.key
 *     (any string / object) names it explicitly, so a lister re-render
 *     cannot resurrect a live button (the new element clicks into the same
 *     key = no-op, and it JOINS the busy state) and a row's other buttons
 *     lock together (one key per row);
 *   - NAVIGATION: opts.navigate: true keeps the busy state after fn resolves
 *     (the page is leaving) - fn resolving `false` or throwing releases it;
 *   - THE DETECTOR (dev environments only, settings.dev): a g.fetch /
 *     g.dialog / g.confirm / g.alert / g.prompt / g.codePrompt started from a
 *     click WITHOUT g.action logs a console.error - the shot tool and the
 *     battery fail on it. The per-call opt-out is { unguarded: true } on the
 *     fetch/dialog options (a call that carries its own guard or is safely
 *     repeatable: a latest-wins read, a lib's seq-guarded load). Clicks
 *     inside a genus dialog are lib-driven (the dialog holds its own guard)
 *     and never flagged.
 *
 * Targets: an element, a selector, a list of elements, or null (lock only,
 * with opts.key). A `.dropdown-item` hosts its spinner on the visible half
 * of its `.btn-group` (the menu closes on click - the spinner must land
 * where the eye is). fn receives the first target.
 *
 * Full reference: docs/libs/genus.md "g.action".
 */
import { settings } from './core.js';
import { busy } from './busy.js';
import { logError } from './log.js';

const locks = new Map(); // key -> { promise, hosts: Set<Element> }

// The spinner's host: a dropdown item's spinner goes on the group's visible half.
function hostOf(el) {
	if (el.classList.contains('dropdown-item')) {
		const group = el.closest('.btn-group, .dropdown');
		const visible = group && group.querySelector(':scope > .btn:not(.dropdown-toggle), :scope > .btn.dropdown-toggle');
		return visible || el;
	}
	return el;
}

function resolveTargets(target) {
	if (!target) return [];
	if (typeof target === 'string') return Array.from(document.querySelectorAll(target));
	if (target instanceof Element) return [target];
	if (typeof target[Symbol.iterator] === 'function') return Array.from(target).filter((t) => t instanceof Element);
	return [];
}

export function action(target, fn, opts = {}) {
	const targets = resolveTargets(target);
	const key = opts.key !== undefined && opts.key !== null ? opts.key : targets[0];
	const hosts = targets.map(hostOf);
	markGuarded();
	if (key !== undefined) {
		const running = locks.get(key);
		if (running) {
			// re-entry: the (possibly re-rendered) control joins the busy state
			for (const h of hosts) {
				if (!running.hosts.has(h)) { busy(h, true); running.hosts.add(h); }
			}
			return running.promise;
		}
	}
	for (const h of hosts) busy(h, true);
	const entry = { hosts: new Set(hosts), promise: null };
	// the lock is taken BEFORE fn starts (fn runs synchronously up to its
	// first await - g.action.locked(key) inside it reads true)
	if (key !== undefined) locks.set(key, entry);
	let keep = false;
	entry.promise = (async () => {
		try {
			const out = await fn(targets[0] || null);
			keep = opts.navigate === true && out !== false;
			return out;
		} finally {
			if (key !== undefined && locks.get(key) === entry) locks.delete(key);
			if (!keep) for (const h of entry.hosts) busy(h, false);
		}
	})();
	return entry.promise;
}

// Is an action holding this lock key right now?
action.locked = (key) => locks.has(key);
// The detector's entry for the other libs (dialog): action.check(kind, what, opts).
action.check = (kind, what, opts) => checkGuard(kind, what, opts);

// ---- the detector ---------------------------------------------------------------

let inClick = false;   // inside a click's task (its microtasks included)
let guarded = false;   // g.action was entered in this click
let clickEl = null;    // the control that was clicked

function markGuarded() {
	guarded = true;
}

function onClick(e) {
	inClick = true;
	guarded = false;
	clickEl = e.target instanceof Element ? e.target : null;
	// a click inside a genus dialog is the LIB's road (its buttons hold their
	// own guards; the awaiting handler continues from here inside its action)
	if (clickEl && clickEl.closest('.genus-dialog')) guarded = true;
	// the task ends after the bubbling listeners and their microtasks; a
	// macrotask runs after all of them
	setTimeout(() => { inClick = false; guarded = false; clickEl = null; }, 0);
}

let attached = false;
export function initActionDetector() {
	if (attached || typeof document === 'undefined') return;
	attached = true;
	// on WINDOW, capture: the event path starts there, so this runs before
	// every document listener — Bootstrap registers its delegated data-api
	// handlers (data-bs-dismiss, data-bs-toggle) on document in the CAPTURE
	// phase (its EventHandler passes the delegation flag as useCapture) and
	// loads before genus; a document listener would see the click AFTER a
	// modal's hide handler already asked its question
	window.addEventListener('click', onClick, true);
}

function labelOf(el) {
	const control = el && el.closest('button, a, [role="button"], input, select, label, [data-action]');
	if (!control) return el ? `<${el.tagName.toLowerCase()}>` : 'the page';
	const text = (control.textContent || '').trim().replace(/\s+/g, ' ');
	return text || control.getAttribute('aria-label') || control.title || control.id || control.value || `<${control.tagName.toLowerCase()}>`;
}

// Called by g.fetch and the dialog lib at their entry: flags a call that a
// click started without g.action. `opts.unguarded` is the explicit opt-out.
export function checkGuard(kind, what, opts) {
	if (!settings.dev || !inClick || guarded) return;
	// the explicit opt-out, and the two fetch roads that ARE guards
	if (opts && (opts.unguarded === true || opts.latest || opts.queue)) return;
	const message = `genus: unguarded action — ${kind} ${what} started from a click on "${labelOf(clickEl)}" without g.action (docs/libs/genus.md "g.action")`;
	console.error(message);
	logError(new Error(message), { type: 'unguarded-action', kind, what });
}
